import { copyObject } from 'src/utils/objects'
import { createReport, createReportRole } from '@/report/store/utils/report'

const actions = {
  errorSaving({ commit }, error) {
    const message = `Error Saving Home: ${error.message}`
    const status = 'error'

    commit('tools/snackbar', true, { root: true })
    commit('tools/snack', { message, status }, { root: true })

    return error
  },

  conformReportForFirestore(context, selectedReport) {
    const {
      associationId,
      baseReportName,
      defaultRole,
      docId,
      index,
      isArchived,
      isClientEnforced,
      isDeleted,
      isFavorite,
      isMetric,
      ownerId,
      parameters,
      reportLabel,
      reportPath,
      roles,
      routeName,
      scope,
      shares,
      subId,
    } = selectedReport

    const conformedParameters = parameters.map((parameter) => {
      const { action, conjunction = 'AND', conditions = [], operator, propertyName, value: inputValue } = parameter
      const isDate = () => ['date', 'dateRange', 'endDate', 'startDate', 'timeEntryDate'].indexOf(propertyName) !== -1
      const value = typeof inputValue === 'string' && isDate() && /,/.test(inputValue) ? inputValue.split(',').map((v) => v.trim()) : inputValue
      return { action, conditions, conjunction, operator, propertyName, value }
    })

    const nav = { baseReportName, routeName }
    const originalQuery = {
      associationId,
      baseReportName,
      defaultRole,
      docId,
      index,
      isArchived,
      isClientEnforced,
      isDeleted,
      isFavorite,
      isMetric,
      ownerId,
      parameters: conformedParameters,
      reportLabel,
      reportPath,
      roles,
      routeName,
      scope,
      shares,
      subId,
    }

    return createReport(nav, originalQuery)
  },

  async upsertReport({ dispatch }, { doc, docId }) {
    const collection = 'report'
    return dispatch('auth/upsertFirestore', { collection, docId, doc }, { root: true })
  },
  handleUpsertToReport({ commit, dispatch, rootGetters, rootState }, { doc, docId }) {
    return dispatch('upsertReport', { doc, docId })
      .then(async () => {
        const message = 'Saved'
        const status = 'success'

        commit('tools/snackbar', true, { root: true })
        commit('tools/snack', { message, status }, { root: true })

        const { isInternalUser, reportCodes } = rootState.auth.user
        const email = rootGetters['auth/userEmail']
        const temporaryReportOptions = []

        await dispatch('auth/setReports', { email, isInternalUser, reportCodes }, { root: true })

        await dispatch('tools/setupNav', null, { root: true })

        commit('tools/temporaryReportOptions', temporaryReportOptions, { root: true })
      })
      .catch((error) => {
        const { message = 'Unknown Error ' } = error
        const status = 'error'

        commit('tools/snackbar', true, { root: true })
        commit('tools/snack', { message, status }, { root: true })
      })
  },

  async updateReport({ dispatch }, report) {
    const { docId } = report
    const doc = await dispatch('conformReportForFirestore', report)

    return dispatch('handleUpsertToReport', { doc, docId })
  },

  async favoriteReport({ dispatch, rootGetters }, selectedReport) {
    const globalTeamReports = rootGetters['tools/globalTeamReports']
    const { docIdOnParent } = selectedReport
    const favoriteReport = docIdOnParent ? globalTeamReports.find((report) => report.docId === docIdOnParent) : selectedReport
    const { docId } = favoriteReport

    favoriteReport.isFavorite = true

    const doc = await dispatch('conformReportForFirestore', favoriteReport)

    return dispatch('handleUpsertToReport', { doc, docId })
  },
  async unfavoriteReport({ dispatch, rootGetters }, selectedReport) {
    const globalTeamReports = rootGetters['tools/globalTeamReports']
    const { docIdOnParent } = selectedReport
    const favoriteReport = docIdOnParent ? globalTeamReports.find((report) => report.docId === docIdOnParent) : selectedReport
    const { docId } = favoriteReport

    favoriteReport.isFavorite = false

    const doc = await dispatch('conformReportForFirestore', favoriteReport)

    return dispatch('handleUpsertToReport', { doc, docId })
  },

  async favoriteReportOnRole({ dispatch, rootGetters }, selectedReport) {
    const userEmail = rootGetters['auth/userEmail']
    const globalTeamReports = rootGetters['tools/globalTeamReports']

    const { docIdOnParent } = selectedReport
    const favoriteReport = docIdOnParent ? globalTeamReports.find((report) => report.docId === docIdOnParent) : selectedReport
    const newReport = copyObject(favoriteReport)
    const { defaultRole, roles, shares } = newReport

    const { docId } = newReport
    const alreadyShared = shares.indexOf(userEmail) !== -1
    const foundRole = roles && roles.find((role) => role.userId === userEmail)

    if (!alreadyShared) {
      newReport.shares.push(userEmail)
    }

    if (foundRole) {
      foundRole.isFavorite = true
    } else {
      const newProperties = {
        isFavorite: false,
        role: defaultRole,
      }
      const newRole = createReportRole(newProperties, userEmail)

      if (!newReport.roles) {
        newReport.roles = [newRole]
      } else {
        newReport.roles.push(newRole)
      }
    }

    const doc = await dispatch('conformReportForFirestore', newReport)

    return dispatch('handleUpsertToReport', { doc, docId })
  },
  async unfavoriteReportOnRole({ dispatch, rootGetters }, selectedReport) {
    const userEmail = rootGetters['auth/userEmail']
    const globalTeamReports = rootGetters['tools/globalTeamReports']

    const { docIdOnParent } = selectedReport
    const favoriteReport = docIdOnParent ? globalTeamReports.find((report) => report.docId === docIdOnParent) : selectedReport
    const newReport = copyObject(favoriteReport)
    const { defaultRole, roles, shares } = newReport

    const { docId } = newReport
    const alreadyShared = shares.indexOf(userEmail) !== -1
    const foundRole = roles && roles.find((role) => role.userId === userEmail)

    if (!alreadyShared) {
      newReport.shares.push(userEmail)
    }

    if (foundRole) {
      foundRole.isFavorite = false
    } else {
      const newProperties = {
        isFavorite: false,
        role: defaultRole,
      }
      const newRole = createReportRole(newProperties, userEmail)

      if (!newReport.roles) {
        newReport.roles = [newRole]
      } else {
        newReport.roles.push(newRole)
      }
    }

    const doc = await dispatch('conformReportForFirestore', newReport)

    return dispatch('handleUpsertToReport', { doc, docId })
  },

  // Replace the current report with the original report from Firestore.
  async restoreReport({ dispatch }, reportToRestore) {
    const { docIdOnParent } = reportToRestore
    const originalReport = await dispatch('auth/getReport', docIdOnParent, { root: true })
    const {
      baseReportName,
      defaultRole,
      index,
      isMetric,
      parameters,
      reportLabel,
      routeName,
    } = originalReport
    const {
      associationId,
      docId,
      isArchived,
      isClientEnforced,
      isDeleted,
      isFavorite,
      ownerId,
      reportPath,
      roles,
      scope,
      shares,
      subId,
    } = reportToRestore

    const restoredReport = {
      associationId,
      baseReportName,
      defaultRole,
      docId,
      index,
      isArchived,
      isClientEnforced,
      isDeleted,
      isFavorite,
      isMetric,
      ownerId,
      parameters,
      reportLabel,
      reportPath,
      roles,
      routeName,
      scope,
      shares,
      subId,
    }

    return dispatch('updateReport', restoredReport)
  },
}

export default actions
